// frontend/src/audio/DenoiseEngine.ts
// Singleton orchestrating the DeepFilterNet3 Web Worker + AudioWorkletNode lifecycle.
//
// Architecture :
//   Main Thread                AudioWorklet Thread         Web Worker Thread
//   -----------                -------------------         ----------------
//   DenoiseEngine              MomentDenoiseProcessor      denoise-worker.ts
//     |                                                       |
//     |-- new Worker() -------------------------------------->|
//     |-- worker.postMessage({type:'init'}) ----------------->|
//     |<-- worker.onmessage({type:'ready'}) -----------------|
//     |                                                       |
//     |-- addModule + new AudioWorkletNode                   |
//     |-- new MessageChannel()                                |
//     |   port1 -> worker.postMessage(                       |
//     |             {type:'attachPort',port:port1},[port1]) >|
//     |   port2 -> node.port.postMessage(                    |
//     |             {type:'port',port:port2},[port2])        |
//     |                                                       |
//     |                       AudioWorkletNode.port2 ------->|
//     |                                process frames -----> |
//     |                                <----- processed -----|
//
// V1 LIMITATION : the Worker holds a single global inference state (analysisBuf,
//                  synthesisBuf, dfHistRe/Im). Only one active AudioWorkletNode is
//                  supported at a time. Last-writer-wins: calling createNode() or
//                  processBatch() while a node already exists tears down the
//                  previous one automatically.
//
// MessagePort transfer caveat : AudioWorkletNode constructor does NOT support a
//                  transfer list for processorOptions (MessagePort is not cloneable
//                  via structured clone). The port MUST be sent via
//                  node.port.postMessage(..., [port]) AFTER construction.

import { denoiseLog } from './denoiseLog'

// Best-effort teardown helper : swallow errors from idempotent cleanup paths
// (disconnect, port.close, Worker.terminate, postMessage on closed channel).
// These all throw if the target is already torn down, and in every call site
// here we explicitly want that to be a no-op — NEVER use this helper to
// silence errors from a real operation path.
const ignoreErrors = (fn: () => void): void => {
  try { fn() } catch { /* expected: idempotent teardown */ }
}

// Vite bundles the worker at build time via new URL(..., import.meta.url).
// Type-only import would break the URL resolution, so we use the runtime import.

// Denoise latency presets
// 'sync':  low-latency batch (10 frames=100ms) — A/V stays in sync
// 'async': high-quality batch (50 frames=500ms) — best denoise quality, A/V may drift
type DenoisePreset = 'fast1' | 'sync' | 'async'
// 'fast1' = aggressive low-latency (5 hops / 2 prebuffer, ~90ms)
// 'sync'  = Fast2, balanced low-latency (10 hops / 3 prebuffer, ~150ms)
// 'async' = Quality, large batches (50 hops / 10 prebuffer, ~560ms)
// ringFrames is unified at 65 (= max of all presets) so switching preset
// at runtime never overflows the worklet ring. A larger ring is just idle
// capacity; prebufferFrames governs the initial prime delay per-preset.
const MAX_RING_FRAMES = 65
const PRESETS: Record<DenoisePreset, { batchSize: number; ringFrames: number; prebufferFrames: number }> = {
  fast1: { batchSize: 5,  ringFrames: MAX_RING_FRAMES, prebufferFrames: 2 },
  sync:  { batchSize: 10, ringFrames: MAX_RING_FRAMES, prebufferFrames: 3 },
  async: { batchSize: 50, ringFrames: MAX_RING_FRAMES, prebufferFrames: 10 },
}
const LS_PRESET_KEY = 'denoise-preset'

function readPreset(): DenoisePreset {
  try {
    const v = localStorage.getItem(LS_PRESET_KEY)
    if (v === 'async') return 'async'
    if (v === 'fast1') return 'fast1'
  } catch { /* */ }
  return 'sync' // default: Fast2 (balanced low latency)
}

class DenoiseEngine {
  private worker: Worker | null = null
  private _ready = false
  private _batchRequestId = 0
  private _loading = false
  private _error: string | null = null
  private readyPromise: Promise<void> | null = null
  private activeNode: AudioWorkletNode | null = null
  private workletModuleLoaded = new WeakSet<AudioContext>()
  private _preset: DenoisePreset = readPreset()

  /**
   * Load the Web Worker and the ONNX models once. Idempotent.
   * Subsequent calls return the same promise.
   */
  async init(): Promise<void> {
    if (this._ready) return
    if (this.readyPromise) return this.readyPromise

    this._loading = true
    this._error = null

    this.readyPromise = new Promise<void>((resolve, reject) => {
      let settled = false
      try {
        this.worker = new Worker(
          new URL('./denoise-worker.ts', import.meta.url),
          { type: 'module' },
        )
      } catch (err) {
        this._loading = false
        this._error = (err as Error).message
        this.readyPromise = null
        reject(err)
        return
      }

      const worker = this.worker
      worker.onmessage = (ev: MessageEvent) => {
        const data = ev.data
        if (!data || typeof data !== 'object') return
        if (data.type === 'ready' && !settled) {
          settled = true
          this._ready = true
          this._loading = false
          resolve()
        } else if (data.type === 'error') {
          const message = typeof data.message === 'string' ? data.message : 'denoise worker error'
          this._error = message
          if (!settled) {
            settled = true
            this._loading = false
            this.readyPromise = null
            reject(new Error(message))
          }
        }
      }

      worker.onerror = (ev: ErrorEvent) => {
        const message = ev.message || 'denoise worker crash'
        this._error = message
        if (!settled) {
          settled = true
          this._loading = false
          this.readyPromise = null
          reject(new Error(message))
        }
      }

      worker.postMessage({ type: 'init' })
    })

    await this.readyPromise
    // Send batch size config to worker based on preset
    const p = PRESETS[this._preset]
    this.worker?.postMessage({ type: 'config', batchSize: p.batchSize })
  }

  /**
   * Create an AudioWorkletNode wired to the shared Worker via a MessageChannel.
   *
   * V1 : only one active node at a time. If a previous node exists, it is
   *      destroyed before the new one is created.
   *
   * Flow :
   *  1. await init()
   *  2. disconnect any previous activeNode
   *  3. addModule(worklet) — idempotent per AudioContext
   *  4. create AudioWorkletNode
   *  5. create MessageChannel; transfer port1 to Worker, port2 to Worklet
   *  6. return the node (caller connects source → node → destination)
   */
  async createNode(ctx: AudioContext): Promise<AudioWorkletNode> {
    await this.init()
    if (!this.worker) throw new Error('DenoiseEngine: worker not ready')

    // Last-writer-wins: tear down any previous node to keep the Worker's global state consistent
    if (this.activeNode) {
      denoiseLog({ step: 'createNode_disconnect', detail: 'last-writer-wins: disconnecting previous node' })
      const prev = this.activeNode
      ignoreErrors(() => prev.disconnect())
      ignoreErrors(() => prev.port.close())
      this.activeNode = null
    }

    // addModule is idempotent but calling it many times on the same context is cheap.
    // We still cache the context to avoid unnecessary network/IO in the bundle path.
    if (!this.workletModuleLoaded.has(ctx)) {
      await ctx.audioWorklet.addModule('/audio/denoise-worklet-processor.js')
      this.workletModuleLoaded.add(ctx)
    }

    const p = PRESETS[this._preset]
    const node = new AudioWorkletNode(ctx, 'moment-denoise-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: { ringFrames: p.ringFrames, prebufferFrames: p.prebufferFrames },
    })

    // MessagePort cannot be cloned via structured clone inside processorOptions.
    // We must create the channel AFTER the node exists and transfer each end
    // explicitly via a postMessage transfer list.
    const channel = new MessageChannel()

    // port1 → Worker (worker will attach its frame handler to this port)
    this.worker.postMessage(
      { type: 'attachPort', port: channel.port1 },
      [channel.port1],
    )

    // port2 → Worklet (worklet will attach its frame handler to this port)
    node.port.postMessage(
      { type: 'port', port: channel.port2 },
      [channel.port2],
    )

    this.activeNode = node
    return node
  }

  /** Set the active node mode ('on' | 'off'). Safe no-op if no active node. */
  setMode(mode: 'on' | 'off'): void {
    if (!this.activeNode) return
    this.activeNode.port.postMessage({ type: 'mode', value: mode })
  }

  /** Current preset name. */
  get preset(): DenoisePreset { return this._preset }

  /** Change preset and persist to localStorage. Takes effect on next createNode(). */
  setPreset(preset: DenoisePreset): void {
    this._preset = preset
    try { localStorage.setItem(LS_PRESET_KEY, preset) } catch { /* */ }
    // Update worker batch size immediately (takes effect on next batch)
    const p = PRESETS[preset]
    this.worker?.postMessage({ type: 'config', batchSize: p.batchSize })
  }

  /** Estimated pipeline latency in ms based on preset.
   *  Values calibrated empirically via AVBench (DN off ~6-10ms, Rapide ~120-141ms, Qualite ~549-560ms).
   *  Used as fallback when no bench profile is stored in localStorage. */
  get estimatedLatencyMs(): number {
    switch (this._preset) {
      case 'fast1': return 90
      case 'sync':  return 150
      case 'async': return 560
      default:      return 0
    }
  }

  /**
   * Reset the Worker's STFT/DF state without unloading models.
   * Called before offline batch processing to clear overlap from previous use.
   */
  async resetState(): Promise<void> {
    await this.init()
    if (!this.worker) throw new Error('DenoiseEngine: worker not ready')

    const worker = this.worker
    return new Promise<void>((resolve, reject) => {
      const onMessage = (ev: MessageEvent) => {
        const data = ev.data
        if (!data || typeof data !== 'object') return
        if (data.type === 'resetDone') {
          worker.removeEventListener('message', onMessage)
          resolve()
        } else if (data.type === 'error') {
          worker.removeEventListener('message', onMessage)
          reject(new Error(typeof data.message === 'string' ? data.message : 'denoise worker error'))
        }
      }
      worker.addEventListener('message', onMessage)
      worker.postMessage({ type: 'reset' })
    })
  }

  /**
   * Process all frames in a single batched ONNX call via the Worker (offline).
   *
   * The Worker batches internally (up to MAX_BATCH=100 frames per ONNX call)
   * so the GRU hidden states see temporal context within each batch.
   *
   * Used by `denoise.file()`. Throws if a real-time node is active.
   *
   * @param packed   Float32Array of frameCount * 480 samples
   * @param frameCount number of 480-sample frames
   * @returns Float32Array of frameCount * 480 clean samples
   */
  async processBatch(packed: Float32Array, frameCount: number, onProgress?: (pct: number) => void): Promise<Float32Array> {
    // Last-writer-wins: tear down any active real-time node so batch can proceed
    if (this.activeNode) {
      denoiseLog({ step: 'processBatch_disconnect', detail: 'last-writer-wins: disconnecting active real-time node' })
      const prev = this.activeNode
      ignoreErrors(() => prev.disconnect())
      ignoreErrors(() => prev.port.close())
      this.activeNode = null
    }
    await this.init()
    if (!this.worker) throw new Error('DenoiseEngine: worker not ready')

    // Each call gets a unique requestId. The Worker aborts stale batches
    // and tags responses so we can ignore results from previous calls.
    const requestId = ++this._batchRequestId

    const worker = this.worker
    return new Promise<Float32Array>((resolve, reject) => {
      const onMessage = (ev: MessageEvent) => {
        const data = ev.data
        if (!data || typeof data !== 'object') return
        // Ignore responses from older/different requests
        if (data.requestId !== undefined && data.requestId !== requestId) return
        if (data.type === 'batchProcessed') {
          worker.removeEventListener('message', onMessage)
          resolve(data.pcm as Float32Array)
        } else if (data.type === 'batchAborted') {
          worker.removeEventListener('message', onMessage)
          reject(new Error('batch_aborted'))
        } else if (data.type === 'batchProgress' && onProgress) {
          const pct = Math.round((data.done / data.total) * 100)
          try { onProgress(pct) } catch { /* progress callback errors are non-fatal */ }
        } else if (data.type === 'error') {
          worker.removeEventListener('message', onMessage)
          reject(new Error(typeof data.message === 'string' ? data.message : 'denoise worker error'))
        }
      }
      worker.addEventListener('message', onMessage)
      try {
        worker.postMessage(
          { type: 'processBatch', pcm: packed, frameCount, requestId, reset: true },
          [packed.buffer],
        )
      } catch (err) {
        worker.removeEventListener('message', onMessage)
        reject(err as Error)
      }
    })
  }

  /**
   * Collect telemetry from Worker (batch RTFs, overruns) and Worklet (underruns,
   * prebuffer time). Returns combined metrics for sendBeacon. Resets accumulators.
   */
  async getMetrics(): Promise<{
    batches: number; audio_s: number; mean_rtf: number; p95_rtf: number;
    max_rtf: number; overruns: number; underruns: number; prebuffer_ms: number;
    errors: number;
  }> {
    const defaults = { batches: 0, audio_s: 0, mean_rtf: 0, p95_rtf: 0, max_rtf: 0, overruns: 0, underruns: 0, prebuffer_ms: 0, errors: 0 }
    if (!this.worker) return defaults

    // Request Worker metrics
    const workerMetrics = await new Promise<Record<string, number>>((resolve) => {
      const timeout = setTimeout(() => resolve({}), 2000)
      const onMsg = (ev: MessageEvent) => {
        if (ev.data?.type === 'metrics') {
          this.worker?.removeEventListener('message', onMsg)
          clearTimeout(timeout)
          resolve(ev.data)
        }
      }
      this.worker!.addEventListener('message', onMsg)
      this.worker!.postMessage({ type: 'getMetrics' })
    })

    // Request Worklet metrics
    let workletMetrics: Record<string, number> = {}
    if (this.activeNode) {
      workletMetrics = await new Promise<Record<string, number>>((resolve) => {
        const timeout = setTimeout(() => resolve({}), 1000)
        const onMsg = (ev: MessageEvent) => {
          if (ev.data?.type === 'metrics') {
            this.activeNode?.port.removeEventListener('message', onMsg)
            clearTimeout(timeout)
            resolve(ev.data)
          }
        }
        this.activeNode!.port.addEventListener('message', onMsg)
        this.activeNode!.port.postMessage({ type: 'getMetrics' })
      })
    }

    return {
      batches: workerMetrics.batches ?? 0,
      audio_s: workerMetrics.audio_s ?? 0,
      mean_rtf: workerMetrics.mean_rtf ?? 0,
      p95_rtf: workerMetrics.p95_rtf ?? 0,
      max_rtf: workerMetrics.max_rtf ?? 0,
      overruns: workerMetrics.overruns ?? 0,
      errors: workerMetrics.errors ?? 0,
      underruns: workletMetrics.underruns ?? 0,
      prebuffer_ms: workletMetrics.prebuffer_ms ?? 0,
    }
  }

  /** Destroy the Worker and release the active node. Resets the engine state. */
  destroy(): void {
    if (this.activeNode) {
      const node = this.activeNode
      ignoreErrors(() => node.disconnect())
      ignoreErrors(() => node.port.close())
      this.activeNode = null
    }
    if (this.worker) {
      const worker = this.worker
      ignoreErrors(() => worker.postMessage({ type: 'destroy' }))
      ignoreErrors(() => worker.terminate())
      this.worker = null
    }
    this._ready = false
    this._loading = false
    this._error = null
    this.readyPromise = null
    this.workletModuleLoaded = new WeakSet<AudioContext>()
  }

  get isReady(): boolean { return this._ready }
  get isLoading(): boolean { return this._loading }
  get error(): string | null { return this._error }
  get hasActiveNode(): boolean { return this.activeNode !== null }
}

export const denoiseEngine = new DenoiseEngine()
export type { DenoiseEngine, DenoisePreset }
